import { useLocation } from "react-router-dom"
import { DataType } from "../../../../tables"
import { define } from "../../../../utils/typeUtils"
import { RoutePath, useRoutesHooks } from "../../../routes"
import { Action, Column, Data, List, ModalId, O, useUIActions } from "../../../ui"
import { RemoveModal } from "../remove-modal/removeModal"
import { useEffect, useMemo, useState } from "react"
import { useStateHooks } from "../../../../state"
import { ArchiveModal } from "../remove-modal/archiveModal"
import { INITIAL_SORT_STATE, SortState, nextSortState, sortRows, useTableSortLabels, withSortHeaders } from "./sortUtils"

type Props = {
    columns: Column[]
    rows?: Data[]
    rowId?: string
    currentDataTypeParam?: DataType
    createLabel?: string
    showDetail?: boolean
    readOnly?: boolean
    minItems?: number
    actions?: (rowIdValue: string) => Action[]
    onCreate?: () => void
    onEdit?: (rowIdValue?: string) => void
    onRemove?: (rowIdValue?: string) => void
    isEditable?: (rowIdValue?: string) => void
    renderDetail?: (row: Data) => JSX.Element
}

export const DataTypeList = (props: Props) => {
    const { columns, rows, rowId = 'id', currentDataTypeParam, actions, createLabel, showDetail = true, readOnly, minItems = 0, onCreate, onEdit, onRemove, isEditable, renderDetail } = props

    const { tableName } = useRoutesHooks().parseUrl()

    const [currentDataType, setCurrentDataType] = useState<O<DataType>>(currentDataTypeParam)
    const [sortState, setSortState] = useState<SortState>(INITIAL_SORT_STATE)

    const { getList } = useStateHooks()
    const dataTypes: DataType[] = getList(tableName!)
    const tableRows = rows || dataTypes as Data[]

    const onColumnSort = (columnName: string) => {
        setSortState(prev => nextSortState(prev, columnName))
    }

    const sortColumn = columns.find(c => c.name === sortState.column)
    const tableSortLabels = useTableSortLabels(sortState.direction ? tableRows : [], sortColumn)

    const sortedTableRows = useMemo((): Data[] => {
        if (!sortState.column || !sortState.direction) return tableRows
        if (!sortColumn || sortColumn.sortable === false) return tableRows
        return sortRows(tableRows, sortColumn, sortState.direction, tableSortLabels)
    }, [tableRows, sortState, sortColumn, tableSortLabels])

    const sortableColumns = withSortHeaders(columns, sortState, onColumnSort)

    const { pathname } = useLocation()
    const basePath = pathname.replace(/\/$/, '')

    const { toggleModal } = useUIActions()

    useEffect(() => {
        setCurrentDataType(currentDataTypeParam)
    }, [currentDataTypeParam])

    const find = (id?: string) => dataTypes.find(dataType => dataType.id === define(id))

    const defaultOnRemove = (id?: string) => {
        setCurrentDataType(find(id))
        toggleModal(ModalId.REMOVE)
    }

    const defaultActions = (rowIdValue?: string): Action[] => {
        const editable = isEditable ? isEditable(rowIdValue) : true

        const actionList: Action[] = []
        if (showDetail) {
            actionList.push({
                icon: 'eye-fill',
                kind: 'tertiary',
                tooltip: 'Ver Detalle',
                href: `${basePath}/${rowIdValue}`
            })
        }
        if (!readOnly) {
            actionList.push({
                icon: 'pencil-fill',
                tooltip: 'Editar',
                disabled: !editable,
                ...(onEdit ? { onClick: onEdit } : { href: `${basePath}/${rowIdValue}/${RoutePath.EDIT}` })
            })
            if (tableRows.length > minItems) {
                actionList.push({
                    icon: 'trash-fill',
                    kind: 'secondary',
                    tooltip: 'Eliminar',
                    disabled: !editable,
                    onClick: onRemove || defaultOnRemove
                })
            }
        }

        return actionList
    }

    const createHref = !onCreate ? `${basePath}/${RoutePath.CREATE}` : undefined

    return (<>
        <List
            columns={sortableColumns}
            rows={sortedTableRows}
            rowId={rowId}
            createLabel={createLabel}
            createHref={createHref}
            readOnly={readOnly}
            actions={actions || defaultActions}
            onCreate={onCreate}
            onEdit={onEdit}
            onRemove={onRemove}
            renderDetail={renderDetail}
        />
        <RemoveModal
            tableName={tableName!}
            dataType={currentDataType}
        />
        <ArchiveModal
            tableName={tableName!}
            dataType={currentDataType}
        />
    </>)
}
